import React, { useState, useEffect } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  IconButton,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import Sidebar from "../../../Navigation/Sidebar/Sidebar";
import marksatteled from "../../../../assests/marksatteled.svg"
import CloseIcon from "@mui/icons-material/Close";
import "./ScheduleReport.css";

// Text truncation component
const TruncatedText = ({ text, maxLength = 20 }) => {
  const [showFullText, setShowFullText] = useState(false);

  if (!text || text.length <= maxLength) {
    return <span>{text || 'N/A'}</span>;
  }

  if (showFullText) {
    return (
      <span>
        {text}
        <button
          className="see-less-btn"
          onClick={() => setShowFullText(false)}
        >
          See Less
        </button>
      </span>
    );
  }

  return (
    <span>
      {text.substring(0, maxLength)}...
      <button
        className="see-more-btn"
        onClick={() => setShowFullText(true)}
      >
        See More
      </button>
    </span>
  );
};

const ScheduleDownloadReportComponent = ({
  loading,
  error,
  reportData,
  formatCurrency,
  onDownloadExcel,
  onGoBack,
  onMarkSettle,
  showMarkSettleDialog,
  setShowMarkSettleDialog,
  selectedDate,
  setSelectedDate,
  onMarkSettledConfirm,
  showSuccessDialog,
  onSuccessClose,
  selectedPayoutId,
  selectedPayoutIds,
  setSelectedPayoutIds,
  selectAll,
  setSelectAll,
  payoutData
}) => {

  const [localSelectAll, setLocalSelectAll] = useState(false);
  const [expandedRows, setExpandedRows] = useState({});

  // Handle select all checkbox
  const handleSelectAll = (event) => {
    const isChecked = event.target.checked;
    setLocalSelectAll(isChecked);

    if (isChecked) {
      // Select all payout IDs
      const allPayoutIds = reportData.cohortScheduleResponseDto?.map(item => item.payoutId) || [];
      setSelectedPayoutIds(allPayoutIds);
    } else {
      // Clear selection
      setSelectedPayoutIds([]);
    }
  };

  // Handle individual checkbox selection
  const handleCheckboxChange = (payoutId) => {
    if (selectedPayoutIds.includes(payoutId)) {
      // Remove from selection
      setSelectedPayoutIds(selectedPayoutIds.filter(id => id !== payoutId));
      setLocalSelectAll(false);
    } else {
      // Add to selection
      setSelectedPayoutIds([...selectedPayoutIds, payoutId]);

      const allPayoutIds = reportData.cohortScheduleResponseDto?.map(item => item.payoutId) || [];
      if (selectedPayoutIds.length + 1 === allPayoutIds.length) {
        setLocalSelectAll(true);
      }
    }
  };

  // Toggle row expansion
  const toggleRowExpansion = (payoutId) => {
    setExpandedRows(prev => ({
      ...prev,
      [payoutId]: !prev[payoutId]
    }));
  };

  // Update local select all when selectedPayoutIds changes
  useEffect(() => {
    const allPayoutIds = reportData.cohortScheduleResponseDto?.map(item => item.payoutId) || [];
    setLocalSelectAll(selectedPayoutIds.length === allPayoutIds.length && allPayoutIds.length > 0);
  }, [selectedPayoutIds, reportData]);

  return (
    <div className="report-page-container">
      {loading ? (
        <div className="loading-spinner">Loading...</div>
      ) : error ? (
        <div className="error-message">{error}</div>
      ) : (
        <>
          <Sidebar />
          <div className="report-content-wrapper">
            <div className="report-header">
              <button className="report-back-btn" onClick={onGoBack}>
                ←
              </button>
              <h1 className="report-date">
                {reportData.date
                  ? new Date(reportData.date).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })
                  : "N/A"}
              </h1>
            </div>
            <div className="report-container">
              {/* Header */}
              <div className="table-container">
                <div className="report-table-wrapper">
                  <table className="report-table">
                    <thead>
                      <tr>
                        <th>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={localSelectAll}
                                onChange={handleSelectAll}
                                disabled={!reportData.cohortScheduleResponseDto || reportData.cohortScheduleResponseDto.length === 0}
                              />
                            }
                            label="Select All"
                          />
                        </th>
                        <th>Partner ID</th>
                        <th>Partner Name</th>
                        <th>Cohort ID</th>
                        
                        <th>Status</th>
                        <th>Deductions</th>
                        <th>Payout</th>
                        <th>Transfer Text</th>

                      </tr>
                    </thead>
                    <tbody>
                      {reportData && reportData.cohortScheduleResponseDto?.map((row) => {
                        const isExpanded = expandedRows[row.payoutId];
                        const hasLongText =
                          (row.payee && row.payee.length > 20) ||
                          (row.transferText && row.transferText.length > 20);

                        return (
                          <React.Fragment key={row.payoutId || row.franchiseeId}>
                            <tr className={isExpanded ? "expanded-row" : ""}>
                              <td>
                                <Checkbox
                                  checked={selectedPayoutIds.includes(row.payoutId)}
                                  onChange={() => handleCheckboxChange(row.payoutId)}
                                />
                              </td>
                              <td>{row.franchiseeId || 'N/A'}</td>
                              <td>

                                {row.payee}
                              </td>
                               <td>

                                {row.cohortId || 'N/A'}
                              </td>
                              <td>
                                <span className={`generatedstatus-${(row.status || 'generated').toLowerCase()}`}>
                                  {row.status || 'Generated'}
                                </span>
                              </td>
                              <td>
                                <TruncatedText text={row.deductions} maxLength={20} />

                              </td>

                              <td>{formatCurrency(row.payout || 0)}</td>
                              <td>
                                <TruncatedText text={row.transferText} maxLength={20} />
                              </td>

                            </tr>

                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
              {/* Footer buttons */}
              <div className="report-footer">
                <Button
                  onClick={onDownloadExcel}
                  variant="outlined"
                  color="success"
                  fullWidth
                >
                  Download
                </Button>
                <Button
                  onClick={() => onMarkSettle(selectedPayoutIds)}
                  variant="contained"
                  color="secondary"
                  className="settle-btn"
                  fullWidth
                  disabled={selectedPayoutIds.length === 0}
                >
                  Mark Settle ({selectedPayoutIds.length})
                </Button>
              </div>
            </div>
          </div>

          {/* Mark Settle Dialog - Only shows when showMarkSettleDialog is true */}
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Dialog
              open={showMarkSettleDialog}
              onClose={() => setShowMarkSettleDialog(false)}
              maxWidth="sm"
              fullWidth={true}
              sx={{
                "& .MuiDialog-paper": {
                  width: "100%",
                  maxWidth: "500px",
                  minHeight: "200px",
                  position: "relative",
                },
              }}
            >
              {/* Header with X button */}
              <DialogTitle sx={{ textAlign: "center", fontWeight: "bold" }}>
                Mark Settle
                <IconButton
                  aria-label="close"
                  onClick={() => setShowMarkSettleDialog(false)}
                  sx={{
                    position: "absolute",
                    right: 8,
                    top: 8,
                    color: "#333"
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </DialogTitle>

              {/* Content */}
              <DialogContent>
                <DatePicker
                  label="Select Date to Mark this Payout as Settled"
                  value={selectedDate}
                  onChange={(newValue) => setSelectedDate(newValue)}
                  renderInput={(params) => (
                    <TextField {...params} fullWidth margin="normal" />
                  )}
                />
              </DialogContent>

              {/* Actions */}
              <DialogActions sx={{ justifyContent: "space-around" }}>

                <Button
                  onClick={onMarkSettledConfirm}
                  color="success"
                  variant="contained"
                  disabled={!selectedDate}
                >
                  DONE
                </Button>
              </DialogActions>
            </Dialog>
          </LocalizationProvider>
        </>
      )}
    </div>
  );
};

export default ScheduleDownloadReportComponent;